/**
 * LandingHero.tsx — Above-fold content only
 *
 * Renders eagerly (part of the main bundle via LandingPage → App.tsx).
 * Contains: sticky nav, hero card, bank logos, CTA buttons, image carousel.
 *
 * Dependencies intentionally kept lean:
 *   ✗ FAQ_DATA (any language)    — BelowFold only
 *   ✗ usePricing                 — BelowFold only
 *   ✗ features / howItWorks data — BelowFold only
 *
 * Slide 1 image is ALWAYS deterministic (see sliderImages.ts) so it
 * matches the <link rel="preload"> hint in index.html.
 *
 * CAROUSEL DEFERRAL
 * ─────────────────
 * The Carousel renders all children inside a flex track that uses
 * CSS transform to slide between them. Even with loading="lazy", browsers
 * fetch off-screen images in transform-shifted containers because they
 * still occupy layout space. On Slow 4G, slides 2–8 (~500 KiB total)
 * compete with the critical path and delay FCP/LCP significantly.
 *
 * Fix: slide 1 renders as a static placeholder on the first paint.
 * After mount, useEffect swaps in the full Carousel with all 8 slides.
 * The swap is invisible — slide 1 looks identical in both phases.
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReactCountryFlag from 'react-country-flag';
import Logo from '../../../components/Logo';
import Button from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Carousel } from '../../../components/data-display/Carousel';
import BankLogosCarousel from '../../components/BankLogosCarousel';
import LanguageSelector from '../../../components/LanguageSelector';
import { getRandomSlideImage } from '../../../utils/sliderImages';
import { useAuthStore } from '../../../stores/authStore';
import { useTranslation } from '../../../i18n';
// analyticsService is loaded lazily — keeping it out of the main bundle
// saves ~18KB. The dynamic import fires on click (user-initiated, not
// time-critical) and the tracking call is fire-and-forget.
const trackCtaClick = (id: string, path: string) =>
  import('../../../services/analyticsService').then(m => m.trackCtaClick(id, path));

export default function LandingHero() {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuthStore();
  const { pathname } = useLocation();
  const onSimulator = pathname.startsWith('/simulator');

  // Slide 1 is always deterministic — matches index.html preload href.
  const [slide1] = useState(() => getRandomSlideImage(1));

  // Slides 2-8 are only needed once the full carousel mounts after first paint.
  // Initialising them at render time is wasteful — they're never shown on slide 1.
  const [otherSlides, setOtherSlides] = useState<{
    slide2: string; slide3: string; slide4: string; slide5: string;
    slide6: string; slide7: string; slide8: string;
  } | null>(null);

  useEffect(() => {
    // Defer carousel mount until after first paint so slides 2-8 images
    // don't compete with the critical-path bandwidth on Slow 4G.
    //
    // IMPORTANT: Skip during pre-rendering. The pre-render plugin waits
    // 5 seconds (renderAfterTime), which is enough for this useEffect to
    // fire and swap the carousel to Phase 2. The browser's initial render
    // starts at Phase 1 (otherSlides = null). If the pre-rendered HTML has
    // Phase 2 but React hydrates expecting Phase 1, React detects a mismatch
    // and falls back to full client-side rendering — adding 2-3 seconds to
    // LCP. Skipping here keeps the pre-rendered HTML at Phase 1, matching
    // the browser's initial render exactly.
    if (/HeadlessChrom/i.test(navigator.userAgent)) return;
    setOtherSlides({
      slide2: getRandomSlideImage(2),
      slide3: getRandomSlideImage(3),
      slide4: getRandomSlideImage(4),
      slide5: getRandomSlideImage(5),
      slide6: getRandomSlideImage(6),
      slide7: getRandomSlideImage(7),
      slide8: getRandomSlideImage(8),
    });
  }, []);

  const trustedCountries = [
    { code: 'US', name: t('landing.countries.US') },
    { code: 'CA', name: t('landing.countries.CA') },
    { code: 'DO', name: t('landing.countries.DO') },
    { code: 'ES', name: t('landing.countries.ES') },
    { code: 'FR', name: t('landing.countries.FR') },
  ];

  return (
    <>
      {/* Navigation Header */}
      <header className="bg-white/80 backdrop-blur-md border-b sticky top-0 z-50 shadow-sm transition-shadow duration-300">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <Link to="/" className="flex items-center hover:opacity-80 transition-opacity" aria-label="HipoTech — go to home page">
                <Logo className="h-8 sm:h-10 md:h-12 w-auto" aria-hidden="true" />
              </Link>
              <span className="hidden sm:inline-flex">
                <Badge variant="warm" size="sm">{t('common.secure')}</Badge>
              </span>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/features" className="text-sm font-medium text-slate-600 hover:text-brand-600 transition-colors">{t('nav.features') || 'Features'}</Link>
              <Link to="/pricing" className="text-sm font-medium text-slate-600 hover:text-brand-600 transition-colors">{t('nav.pricing') || 'Pricing'}</Link>
              <Link to="/faq" className="text-sm font-medium text-slate-600 hover:text-brand-600 transition-colors">{t('nav.faq') || 'FAQ'}</Link>
            </nav>
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <div className="h-6 w-px bg-slate-300 hidden sm:block"></div>
              {isAuthenticated ? (
                <>
                  <Link
                    to={user?.role === 'CUSTOMER' ? '/customer/dashboard' : '/admin/dashboard'}
                    className="btn btn-outline btn-sm"
                  >
                    {t('nav.dashboard')}
                  </Link>
                  <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-600 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <span className="text-sm font-medium text-slate-700">{user?.name || 'User'}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={logout} className="text-slate-500 hover:text-slate-700">
                    {t('nav.logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/customer/login" className="btn btn-outline btn-sm">
                    {t('nav.login')}
                  </Link>
                  <Link to="/customer/apply" className="btn btn-primary btn-sm hidden sm:inline-flex" onClick={() => trackCtaClick('nav_get_started', '/customer/register')}>
                    {t('common.getStarted')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section — no entrance animations on above-fold content.
          animate-slideUp and animate-fadeIn start at opacity:0, which hides
          pre-rendered text and causes Lighthouse to measure a 2.7s "element
          render delay" as the animation plays. On slow 4G, the post-hydration
          opacity:0→1 transition also tanks Speed Index by creating a visible
          flash (content appears → disappears → reappears). Removed entirely
          for above-fold; below-fold animations are fine since they don't
          affect LCP or SI. */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20">
        <div className="text-center max-w-4xl mx-auto mb-10">
          {/* Eyebrow — short, human-friendly, draws the eye first */}
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-brand-300" aria-hidden="true" />
            <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-widest uppercase text-brand-600">
              {t('landing.hero.subtitle')}
              <ReactCountryFlag
                countryCode="DO"
                svg
                cdnUrl="/flags/4x3/"
                style={{ width: '1.1em', height: '1.1em', verticalAlign: 'middle' }}
                title="Dominican Republic"
              />
            </span>
            <span className="h-px w-10 bg-brand-300" aria-hidden="true" />
          </div>
          {/* h1 — SEO title, all keywords preserved */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6 tracking-tight">
            {t('landing.hero.title')}
          </h1>
          {/* Description paragraph — full SEO text stays in DOM */}
          <p className="text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
            {t('landing.hero.description')}
          </p>
        </div>

        {/* Bank Logos Carousel
             Normal mode: hidden (no banks have carouselVisible=true yet)
             Demo mode (?secret=365247): shows all 13 banks with logos */}
        <div className="mt-12 mb-12">
          <BankLogosCarousel countryCode="DO" />
        </div>

        <div className="flex flex-col sm:flex-row gap-md justify-center mt-xl">
          <Link to="/customer/apply" className="btn btn-primary btn-lg" onClick={() => trackCtaClick('hero_primary', '/customer/register')}>
            {t('landing.hero.cta')} →
          </Link>
          <Link to="/learn-more" className="btn btn-secondary btn-lg" onClick={() => trackCtaClick('hero_secondary', '/learn-more')}>
            {t('landing.hero.secondaryCta')}
          </Link>
        </div>
      </div>

      {/* Hero Carousel
          ─────────────────────────────────────────────────────────────────
          Phase 1 (first render): Only slide 1 — a plain <img> in a matching
          container. Zero competing image downloads on the critical path.

          Phase 2 (after useEffect fires): otherSlides is populated and the
          full 8-slide Carousel replaces the placeholder. The transition is
          invisible because slide 1 looks identical in both phases.
          ─────────────────────────────────────────────────────────────────
      */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-12 md:pb-20">
        <div className="mt-xl overflow-hidden rounded-lg">
          {!otherSlides ? (
            /* Phase 1: static slide 1 only */
            <div className="rounded-lg overflow-hidden shadow-xl relative w-full h-96 bg-gradient-to-br from-blue-500 to-blue-700">
              <img
                src={slide1}
                srcSet={`${slide1.replace('-min.webp', '-480w.webp')} 480w, ${slide1} 1920w`}
                sizes="(max-width: 640px) calc(100vw - 2rem), (max-width: 1024px) calc(100vw - 3rem), 1216px"
                alt={t('landing.hardcoded.altFastBankResponse') || 'HipoTech DR mortgage platform — fast bank response time'}
                className="w-full h-full object-cover opacity-40"
                fetchPriority="high"
                width="1920"
                height="591"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-white/90 backdrop-blur-sm rounded-xl p-xl max-w-lg text-center shadow-2xl">
                  <div className="text-5xl font-bold text-primary mb-sm">{t('landing.stats.responseTime')}</div>
                  <div className="text-xl font-semibold text-slate-700 mb-xs">{t('landing.stats.responseTimeLabel')}</div>
                  <p className="text-slate-600">
                    {t('landing.features.support.description')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Phase 2: full carousel */
            <Carousel
              orientation="horizontal"
              autoPlay={true}
              autoPlayInterval={6000}
              showNavigation={true}
              className="rounded-lg overflow-hidden shadow-xl"
            >
              {/* Slide 1: Fast Response */}
              <div className="relative w-full h-96 bg-gradient-to-br from-blue-500 to-blue-700">
                <img
                  src={slide1}
                  srcSet={`${slide1.replace('-min.webp', '-480w.webp')} 480w, ${slide1} 1920w`}
                  sizes="(max-width: 640px) calc(100vw - 2rem), (max-width: 1024px) calc(100vw - 3rem), 1216px"
                  alt={t('landing.hardcoded.altFastBankResponse') || 'HipoTech DR mortgage platform — fast bank response time'}
                  className="w-full h-full object-cover opacity-40"
                  fetchPriority="high"
                  width="1920"
                  height="591"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-xl max-w-lg text-center shadow-2xl">
                    <div className="text-5xl font-bold text-primary mb-sm">{t('landing.stats.responseTime')}</div>
                    <div className="text-xl font-semibold text-slate-700 mb-xs">{t('landing.stats.responseTimeLabel')}</div>
                    <p className="text-slate-600">
                      {t('landing.features.support.description')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Slide 2: Blog — Interest Rates */}
              <div className="relative w-full h-96 bg-gradient-to-br from-amber-500 to-orange-700">
                <img
                  src={otherSlides.slide4}
                  alt={t('landing.blogSlides.interestRates.title') || 'Why Are DR Interest Rates So High?'}
                  className="w-full h-full object-cover opacity-40"
                  loading="lazy"
                  width="1920"
                  height="591"
                />
                <div className="absolute inset-0 flex items-center justify-start px-8 md:px-16">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-lg sm:p-xl max-w-sm shadow-2xl">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 mb-sm">
                      {t('landing.blogSlides.badge') || 'BLOG'}
                    </span>
                    <div className="text-xl sm:text-2xl font-bold text-slate-800 mb-xs">
                      {t('landing.blogSlides.interestRates.title') || 'Why Are DR Interest Rates So High?'}
                    </div>
                    <p className="text-slate-600 text-sm sm:text-base mb-md">
                      {t('landing.blogSlides.interestRates.teaser') || 'A deeper look reveals the story is more complex than it first appears.'}
                    </p>
                    <Link
                      to="/blog/interest-rates-dr"
                      className="inline-block px-5 py-2 rounded-lg bg-amber-700 text-white text-sm font-medium hover:bg-amber-800 transition-[opacity]"
                      aria-label={t('landing.hardcoded.readWhyAreDrInterest') || 'Read: Why Are DR Interest Rates So High?'}
                    >
                      {t('landing.blogSlides.interestRates.readLink') || 'Read: Why Are DR Interest Rates So High?'} →
                    </Link>
                  </div>
                </div>
              </div>

              {/* Slide 3: Multiple Banks */}
              <div className="relative w-full h-96 bg-gradient-to-br from-green-500 to-teal-700">
                <img
                  src={otherSlides.slide2}
                  alt={t('landing.hardcoded.altMultiBankSubmission') || 'HipoTech submits your Dominican Republic mortgage application to multiple banks simultaneously'}
                  className="w-full h-full object-cover opacity-40"
                  loading="lazy"
                  width="1920"
                  height="591"
                />
                <div className="absolute inset-0 flex items-center justify-end px-8 md:px-16">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-xl max-w-sm shadow-2xl text-right">
                    <div className="text-5xl font-bold text-green-600 mb-sm">{t('landing.stats.banksCount')}</div>
                    <div className="text-xl font-semibold text-slate-700 mb-xs">{t('landing.features.multiBank.title')}</div>
                    <p className="text-slate-600">
                      {t('landing.features.multiBank.description')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Slide 4: Blog — Property Appreciation */}
              <div className="relative w-full h-96 bg-gradient-to-br from-emerald-500 to-cyan-700">
                <img
                  src={otherSlides.slide5}
                  alt={t('landing.blogSlides.appreciation.title') || 'Rates Are High — But So Are Returns'}
                  className="w-full h-full object-cover opacity-40"
                  loading="lazy"
                  width="1920"
                  height="591"
                />
                <div className="absolute inset-0 flex items-center justify-start px-8 md:px-16">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-lg sm:p-xl max-w-sm shadow-2xl">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 mb-sm">
                      {t('landing.blogSlides.badge') || 'BLOG'}
                    </span>
                    <div className="text-xl sm:text-2xl font-bold text-slate-800 mb-xs">
                      {t('landing.blogSlides.appreciation.title') || 'Rates Are High — But So Are Returns'}
                    </div>
                    <p className="text-slate-600 text-sm sm:text-base mb-md">
                      {t('landing.blogSlides.appreciation.teaser') || 'What every North American buyer should know before walking away from a DR mortgage.'}
                    </p>
                    <Link
                      to="/blog/property-appreciation"
                      className="inline-block px-5 py-2 rounded-lg bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 transition-[opacity]"
                      aria-label={t('landing.hardcoded.readRatesAreHigh') || 'Read: Rates Are High — But So Are Returns'}
                    >
                      {t('landing.blogSlides.appreciation.readLink') || 'Read: Rates Are High — But So Are Returns'} →
                    </Link>
                  </div>
                </div>
              </div>

              {/* Slide 5: Zero Storage */}
              <div className="relative w-full h-96 bg-gradient-to-br from-purple-500 to-indigo-700">
                <img
                  src={otherSlides.slide3}
                  alt={t('landing.hardcoded.altZeroPersistence') || 'HipoTech zero-persistence security — sensitive mortgage data is never stored'}
                  className="w-full h-full object-cover opacity-40"
                  loading="lazy"
                  width="1920"
                  height="591"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-xl max-w-lg text-center shadow-2xl">
                    <div className="text-5xl font-bold text-purple-600 mb-sm">100%</div>
                    <div className="text-xl font-semibold text-slate-700 mb-xs">{t('landing.features.secure.title')}</div>
                    <p className="text-slate-600">
                      {t('landing.features.secure.description')}
                    </p>
                    <Link
                      to="/diligence/buyers#after-submit"
                      className="no-underline inline-block text-sm text-purple-600 hover:text-purple-700 font-medium mt-3"
                    >
                      {t('landing.features.secure.maskingLink') || 'See how we protect your documents'} →
                    </Link>
                  </div>
                </div>
              </div>

              {/* Slide 6: Blog — Bank Capital */}
              <div className="relative w-full h-96 bg-gradient-to-br from-rose-500 to-pink-700">
                <img
                  src={otherSlides.slide6}
                  alt={t('landing.blogSlides.bankCapital.title') || 'Do Banks Have the Capital?'}
                  className="w-full h-full object-cover opacity-40"
                  loading="lazy"
                  width="1920"
                  height="591"
                />
                <div className="absolute inset-0 flex items-center justify-end px-8 md:px-16">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-lg sm:p-xl max-w-sm shadow-2xl text-right">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 mb-sm">
                      {t('landing.blogSlides.badge') || 'BLOG'}
                    </span>
                    <div className="text-xl sm:text-2xl font-bold text-slate-800 mb-xs">
                      {t('landing.blogSlides.bankCapital.title') || 'Do Banks Have the Capital?'}
                    </div>
                    <p className="text-slate-600 text-sm sm:text-base mb-md">
                      {t('landing.blogSlides.bankCapital.teaser') || 'The DR mortgage market is one of the least saturated in the Western Hemisphere.'}
                    </p>
                    <Link
                      to="/blog/bank-capital"
                      className="inline-block px-5 py-2 rounded-lg bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 transition-[opacity]"
                      aria-label={t('landing.hardcoded.readDoDominicanBanks') || 'Read: Do Dominican Banks Have the Capital to Lend?'}
                    >
                      {t('landing.blogSlides.bankCapital.readLink') || 'Read: Do Dominican Banks Have the Capital?'} →
                    </Link>
                  </div>
                </div>
              </div>

              {/* Slide 7: Marketing — One Fee */}
              <div className="relative w-full h-96 bg-gradient-to-br from-teal-500 to-cyan-700">
                <img
                  src={otherSlides.slide7}
                  alt={t('landing.hardcoded.altTransparentPricing') || 'HipoTech transparent pricing — one fee covers your entire DR mortgage application'}
                  className="w-full h-full object-cover opacity-40"
                  loading="lazy"
                  width="1920"
                  height="591"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-xl max-w-lg text-center shadow-2xl">
                    <div className="text-5xl font-bold text-teal-600 mb-sm">{t('landing.pricing.title')}</div>
                    <div className="text-xl font-semibold text-slate-700 mb-xs">{t('landing.pricing.subtitle')}</div>
                  </div>
                </div>
              </div>

              {/* Slide 8: Blog — Daily Rentals */}
              <div className="relative w-full h-96 bg-gradient-to-br from-sky-500 to-indigo-700">
                <img
                  src={otherSlides.slide8}
                  alt={t('landing.blogSlides.dailyRentals.title') || 'Buy a Property, Rent It Daily'}
                  className="w-full h-full object-cover opacity-40"
                  loading="lazy"
                  width="1920"
                  height="591"
                />
                <div className="absolute inset-0 flex items-center justify-start px-8 md:px-16">
                  <div className="bg-white/90 backdrop-blur-sm rounded-xl p-lg sm:p-xl max-w-sm shadow-2xl">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-sky-100 text-sky-700 mb-sm">
                      {t('landing.blogSlides.badge') || 'BLOG'}
                    </span>
                    <div className="text-xl sm:text-2xl font-bold text-slate-800 mb-xs">
                      {t('landing.blogSlides.dailyRentals.title') || 'Buy a Property, Rent It Daily'}
                    </div>
                    <p className="text-slate-600 text-sm sm:text-base mb-md">
                      {t('landing.blogSlides.dailyRentals.teaser') || "11.6 million visitors and no STR restrictions — the investor's guide to short-term rentals."}
                    </p>
                    <Link
                      to="/blog/daily-rentals"
                      className="inline-block px-5 py-2 rounded-lg bg-sky-700 text-white text-sm font-medium hover:bg-sky-800 transition-[opacity]"
                      aria-label={t('landing.hardcoded.readBuyPropertyInDr') || 'Read: Buy Property in DR, Rent It Daily — STR & CONFOTUR Guide'}
                    >
                      {t('landing.blogSlides.dailyRentals.readLink') || 'Read: STR & CONFOTUR Investor Guide'} →
                    </Link>
                  </div>
                </div>
              </div>
            </Carousel>
          )}
        </div>
      </div>
    </>
  );
}